import { useState, useEffect, useMemo, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiTrendingUp, FiTrendingDown, FiCreditCard, FiDollarSign,
    FiBarChart2, FiPieChart, FiActivity, FiUser, FiFilter,
    FiRefreshCw, FiX, FiCheck, FiCalendar, FiChevronDown,
    FiChevronUp, FiArrowUpRight, FiArrowDownRight, FiAward,
    FiPackage, FiAlertCircle, FiMaximize2
} from "react-icons/fi";
import { transactionsAPI, usersAPI, bankAccountsAPI } from "services/api.js";
import { useAuth } from "context/AuthContext";

// ============================================================
// YARDIMCI FONKSİYONLAR
// ============================================================
const formatCurrency = (value) =>
    (Number(value) || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY" });

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const [year, month, day] = dateString.split("T")[0].split("-");
        return `${day}.${month}.${year}`;
    } catch { return dateString; }
};

const getMonthName = (monthNum) => {
    const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
    return months[monthNum - 1] || "";
};

const CHART_COLORS = {
    income: "#10b981",
    expense: "#ef4444",
    brand: "#4318FF",
    blue: "#0ea5e9",
    purple: "#a855f7",
    amber: "#f59e0b",
    cyan: "#06b6d4",
    orange: "#f97316",
};

const STAFF_PALETTE = [
    "#4318FF","#0ea5e9","#10b981","#f59e0b","#ef4444",
    "#a855f7","#06b6d4","#f97316","#84cc16","#ec4899",
];

// ============================================================
// APEXCHARTS ORTAK TEMası
// ============================================================
const useChartTheme = () => {
    const isDark = document.documentElement.classList.contains("dark");
    return {
        textColor: isDark ? "#94a3b8" : "#64748b",
        gridColor: isDark ? "rgba(148,163,184,0.08)" : "rgba(148,163,184,0.15)",
        bgColor: "transparent",
        tooltipBg: isDark ? "#1B254B" : "#fff",
        tooltipText: isDark ? "#E0E5F2" : "#1B254B",
    };
};

// ============================================================
// SKELETON LOADERLAR
// ============================================================
const KPISkeleton = () => (
    <div className="animate-pulse bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 p-5 h-32 flex flex-col justify-between">
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-navy-700"></div>
            <div className="space-y-2 flex-1">
                <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-1/2"></div>
                <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-1/3"></div>
            </div>
        </div>
        <div className="h-6 bg-gray-200 dark:bg-navy-700 rounded-xl w-2/3"></div>
    </div>
);

const ChartSkeleton = ({ height = 300 }) => (
    <div className="animate-pulse flex flex-col items-center justify-center bg-gray-50 dark:bg-navy-900/50 rounded-2xl" style={{ height }}>
        <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-navy-700 mb-4"></div>
        <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-1/3 mb-2"></div>
        <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-1/4"></div>
    </div>
);

const TableSkeleton = () => (
    <div className="space-y-3 p-4">
        {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900">
                <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-navy-700 shrink-0"></div>
                <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-3/4"></div>
                    <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-1/2"></div>
                </div>
                <div className="h-5 w-20 bg-gray-200 dark:bg-navy-700 rounded-lg shrink-0"></div>
            </div>
        ))}
    </div>
);

// ============================================================
// KPI KARTI
// ============================================================
const KpiCard = ({ title, value, subtitle, icon, gradient, trend }) => (
    <div className={`relative overflow-hidden rounded-3xl p-5 text-white shadow-lg`}
         style={{ background: gradient }}>
        {/* Dekor halka */}
        <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full opacity-20 bg-white/30"></div>
        <div className="absolute -right-2 -bottom-6 w-16 h-16 rounded-full opacity-10 bg-white/30"></div>

        <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    {icon}
                </div>
                {trend !== undefined && (
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full backdrop-blur-sm
            ${trend >= 0 ? "bg-green-500/20 text-green-100" : "bg-red-500/20 text-red-100"}`}>
            {trend >= 0 ? "+" : ""}{trend}
          </span>
                )}
            </div>
            <div className="text-2xl font-extrabold leading-tight mb-0.5">{value}</div>
            <div className="text-xs font-bold opacity-80 uppercase tracking-widest">{title}</div>
            {subtitle && (
                <div className="mt-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/20 w-fit">{subtitle}</div>
            )}
        </div>
    </div>
);

// ============================================================
// KPI KARTI (Beyaz / Nötr)
// ============================================================
const KpiCardLight = ({ title, value, subtitle, icon, iconBg, valueColor = "text-navy-700 dark:text-white" }) => (
    <div className="bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 p-5 shadow-sm hover:shadow-md transition-all">
        <div className="flex items-start justify-between mb-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${iconBg}`}>
                {icon}
            </div>
        </div>
        <div className={`text-2xl font-extrabold ${valueColor} mb-1`}>{value}</div>
        <div className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{title}</div>
        {subtitle && (
            <div className="mt-2 text-[11px] font-bold text-gray-400 dark:text-gray-500">{subtitle}</div>
        )}
    </div>
);

// ============================================================
// CARD WRAPPER
// ============================================================
const SectionCard = ({ title, subtitle, icon, iconBg, children, className = "" }) => (
    <div className={`bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden ${className}`}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-navy-700 flex items-center gap-3">
            {icon && (
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg || "bg-brand-100 dark:bg-brand-900/20"}`}>
                    {icon}
                </div>
            )}
            <div>
                <h3 className="font-bold text-navy-700 dark:text-white text-base">{title}</h3>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
        </div>
        {children}
    </div>
);

// ============================================================
// TOP İŞLEM SATIRI
// ============================================================
const TopTxRow = ({ tx, idx, type, staffMap, bankMap }) => {
    const isIncome = type === "income";
    const rankColors = ["#f59e0b", "#94a3b8", "#cd7c4b"];
    const rankColor = idx < 3 ? rankColors[idx] : undefined;

    return (
        <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-navy-700/30 transition-all group">
            {/* Sıra */}
            <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold shrink-0 text-white"
                style={{ backgroundColor: rankColor || (isIncome ? "#10b981" : "#ef4444") }}
            >
                {idx + 1}
            </div>

            {/* İçerik */}
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-navy-700 dark:text-white truncate">{tx.title}</div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <FiCalendar size={10} /> {formatDate(tx.date)}
          </span>
                    {tx.staff_id && staffMap.get(tx.staff_id) && (
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
              <FiUser size={10} /> {staffMap.get(tx.staff_id)}
            </span>
                    )}
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md
            ${tx.payment_method === "card"
                        ? "bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                        : "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"}`}>
            {tx.payment_method === "card"
                ? `Kart${tx.bank_account_id && bankMap.get(tx.bank_account_id) ? ` (${bankMap.get(tx.bank_account_id)})` : ""}`
                : "Nakit"}
          </span>
                </div>
            </div>

            {/* Tutar */}
            <span className={`text-sm font-extrabold shrink-0 ${isIncome ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
        {isIncome ? "+" : "-"}{formatCurrency(tx.amount)}
      </span>
        </div>
    );
};

// ============================================================
// PERSONEL PERFORMANS SATIRI
// ============================================================
const StaffPerfRow = ({ staff, maxIncome }) => {
    const pct = maxIncome > 0 ? (staff.income / maxIncome) * 100 : 0;
    return (
        <div className="p-4 rounded-2xl border border-gray-100 dark:border-navy-700 bg-gray-50/50 dark:bg-navy-900/50 hover:shadow-sm transition-all">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {staff.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-navy-700 dark:text-white truncate">{staff.name}</div>
                    <div className="text-[11px] text-gray-400 dark:text-gray-500">{staff.count} işlem</div>
                </div>
                <div className={`text-sm font-extrabold ${staff.net >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                    {formatCurrency(staff.net)}
                </div>
            </div>

            {/* Gelir/Gider Bar */}
            <div className="relative h-1.5 bg-gray-200 dark:bg-navy-700 rounded-full overflow-hidden">
                <div
                    className="absolute left-0 top-0 h-full rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${pct}%` }}
                ></div>
            </div>

            <div className="flex justify-between mt-2 text-[11px] font-semibold">
                <span className="text-emerald-600 dark:text-emerald-400">↑ {formatCurrency(staff.income)}</span>
                <span className="text-red-500 dark:text-red-400">↓ {formatCurrency(staff.expense)}</span>
            </div>
        </div>
    );
};

// ============================================================
// SELECT BILEŞENI (Modern)
// ============================================================
const ModernSelect = ({ name, value, onChange, options, disabled = false, className = "" }) => (
    <div className={`relative ${className}`}>
        <select
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className="w-full appearance-none bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 pr-9 py-2.5 text-sm font-semibold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">▾</div>
    </div>
);

// ============================================================
// ANA BILEŞEN
// ============================================================
const Admin_Transaction_Analytics = () => {
    // --- STATE ---
    const [allTransactions, setAllTransactions] = useState([]);
    const [summaryData, setSummaryData] = useState({
        total_income: 0, total_expense: 0, net_profit: 0, total_net_income: 0,
    });
    const [staffMap, setStaffMap] = useState(new Map());
    const [staffList, setStaffList] = useState([]);
    const [bankList, setBankList] = useState([]);
    const [bankMap, setBankMap] = useState(new Map());

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterOpen, setFilterOpen] = useState(true);

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const initialFilters = {
        filterType: "month",
        year: currentYear,
        month: currentMonth,
        start_date: "",
        end_date: "",
        staff_id: "all",
        type: "all",
        payment_method: "",
        bank_account_id: "",
    };

    const [activeFilters, setActiveFilters] = useState(initialFilters);
    const [tempFilters, setTempFilters] = useState(initialFilters);

    const { logout } = useAuth();
    const handleAuthError = useCallback(() => { logout(); }, [logout]);

    // ============================================================
    // VERİ ÇEKME
    // ============================================================
    const fetchAnalyticsData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const apiFilters = {};

            if (activeFilters.filterType === "month") {
                const s = new Date(activeFilters.year, activeFilters.month - 1, 1);
                const e = new Date(activeFilters.year, activeFilters.month, 0);
                apiFilters.start_date = s.toISOString().split("T")[0];
                apiFilters.end_date = e.toISOString().split("T")[0];
            } else if (activeFilters.filterType === "range") {
                if (activeFilters.start_date) apiFilters.start_date = activeFilters.start_date;
                if (activeFilters.end_date) apiFilters.end_date = activeFilters.end_date;
            } else if (activeFilters.filterType === "year") {
                apiFilters.start_date = `${activeFilters.year}-01-01`;
                apiFilters.end_date = `${activeFilters.year}-12-31`;
            }

            if (activeFilters.staff_id !== "all") apiFilters.staff_id = activeFilters.staff_id;
            if (activeFilters.type !== "all") apiFilters.type = activeFilters.type;
            if (activeFilters.payment_method) apiFilters.payment_method = activeFilters.payment_method;
            if (activeFilters.bank_account_id) apiFilters.bank_account_id = activeFilters.bank_account_id;

            const [transRes, summaryRes, staffRes, bankRes] = await Promise.all([
                transactionsAPI.getAll(apiFilters),
                transactionsAPI.getSummary(apiFilters),
                usersAPI.getStaff(),
                bankAccountsAPI.getAll(),
            ]);

            if (transRes.success) setAllTransactions(transRes.transactions || []);
            else throw new Error("İşlemler yüklenemedi.");

            if (summaryRes.success) setSummaryData(summaryRes.summary);
            else throw new Error("Özet verisi yüklenemedi.");

            if (staffRes.success) {
                const newMap = new Map();
                (staffRes.staff || []).forEach(s => newMap.set(s.id, `${s.name} ${s.surname}`));
                setStaffMap(newMap);
                setStaffList(staffRes.staff || []);
            }

            if (bankRes.success) {
                setBankList(bankRes.bank_accounts || []);
                const bMap = new Map();
                (bankRes.bank_accounts || []).forEach(b => bMap.set(b.id, b.name));
                setBankMap(bMap);
            }

        } catch (err) {
            console.error("Analiz verisi hatası:", err);
            if (String(err.message).includes("401") || String(err.message).includes("Unauthorized")) {
                handleAuthError(); return;
            }
            setError(err.message || "Veriler yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [activeFilters, handleAuthError]);

    useEffect(() => { fetchAnalyticsData(); }, [fetchAnalyticsData]);

    // --- FİLTRE DEĞİŞİKLİĞİ ---
    const handleTempFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => {
            const next = { ...prev, [name]: value };
            if (name === "bank_account_id" && value !== "") next.payment_method = "card";
            if (name === "payment_method" && (value === "cash" || value === "")) next.bank_account_id = "";
            return next;
        });
    };

    const applyFilters = () => { setActiveFilters(tempFilters); };
    const resetFilters = () => { setTempFilters(initialFilters); setActiveFilters(initialFilters); };

    // ============================================================
    // HESAPLAMALAR
    // ============================================================
    const calculatedMetrics = useMemo(() => {
        const txs = allTransactions || [];
        const incomeTxs = txs.filter(t => t.type === "income");
        const expenseTxs = txs.filter(t => t.type === "expense");
        const avg = txs.length > 0 ? txs.reduce((s, t) => s + Number(t.amount), 0) / txs.length : 0;
        const maxIncome = incomeTxs.length > 0 ? Math.max(...incomeTxs.map(t => Number(t.amount))) : 0;
        const maxExpense = expenseTxs.length > 0 ? Math.max(...expenseTxs.map(t => Number(t.amount))) : 0;
        const totalFees = summaryData.total_income - (summaryData.total_net_income || summaryData.total_income);
        return {
            totalTransactions: txs.length,
            avgTransaction: avg,
            maxIncome,
            maxExpense,
            incomeCount: incomeTxs.length,
            expenseCount: expenseTxs.length,
            totalFees: Math.max(0, totalFees),
        };
    }, [allTransactions, summaryData]);

    // ============================================================
    // GRAFİK VERİLERİ (APEXCHARTS formatı)
    // ============================================================

    // 1. Günlük trend (line chart)
    const dailyTrendSeries = useMemo(() => {
        const txs = allTransactions || [];
        const dailyMap = new Map();
        txs.forEach(tx => {
            const date = tx.date.split("T")[0];
            if (!dailyMap.has(date)) dailyMap.set(date, { income: 0, expense: 0 });
            const d = dailyMap.get(date);
            if (tx.type === "income") d.income += Number(tx.amount);
            else d.expense += Number(tx.amount);
        });
        const sortedDates = Array.from(dailyMap.keys()).sort();
        return {
            categories: sortedDates.map(d => formatDate(d)),
            series: [
                { name: "Gelir (TL)", data: sortedDates.map(d => +(dailyMap.get(d).income.toFixed(2))) },
                { name: "Gider (TL)", data: sortedDates.map(d => +(dailyMap.get(d).expense.toFixed(2))) },
            ],
        };
    }, [allTransactions]);

    // 2. Aylık karşılaştırma (bar chart — sadece yıllık filtrede)
    const monthlyBarSeries = useMemo(() => {
        if (activeFilters.filterType !== "year") return null;
        const txs = allTransactions || [];
        const mm = new Map();
        for (let i = 1; i <= 12; i++) mm.set(i, { income: 0, expense: 0 });
        txs.forEach(tx => {
            const m = new Date(tx.date).getMonth() + 1;
            const d = mm.get(m);
            if (tx.type === "income") d.income += Number(tx.amount);
            else d.expense += Number(tx.amount);
        });
        return {
            categories: Array.from({ length: 12 }, (_, i) => getMonthName(i + 1)),
            series: [
                { name: "Gelir (TL)", data: Array.from({ length: 12 }, (_, i) => +(mm.get(i + 1).income.toFixed(2))) },
                { name: "Gider (TL)", data: Array.from({ length: 12 }, (_, i) => +(mm.get(i + 1).expense.toFixed(2))) },
            ],
        };
    }, [allTransactions, activeFilters.filterType]);

    // 3. Gelir/Gider donut
    const donutSeries = useMemo(() => {
        const { total_income, total_expense } = summaryData;
        const hasData = total_income > 0 || total_expense > 0;
        return {
            series: hasData ? [+total_income.toFixed(2), +total_expense.toFixed(2)] : [1, 0],
            labels: ["Gelirler", "Giderler"],
            hasData,
        };
    }, [summaryData]);

    // 4. Personel bazlı analiz (horizontal bar)
    const staffBarSeries = useMemo(() => {
        const txs = allTransactions || [];
        const staffIncomeMap = new Map();
        txs.forEach(tx => {
            if (tx.type === "income" && tx.staff_id) {
                staffIncomeMap.set(tx.staff_id, (staffIncomeMap.get(tx.staff_id) || 0) + Number(tx.amount));
            }
        });
        const sorted = Array.from(staffIncomeMap.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10);
        return {
            categories: sorted.map(([id]) => staffMap.get(id) || "Bilinmeyen"),
            series: [{ name: "Gelir (TL)", data: sorted.map(([, v]) => +v.toFixed(2)) }],
            colors: sorted.map((_, i) => STAFF_PALETTE[i % STAFF_PALETTE.length]),
            hasData: sorted.length > 0,
        };
    }, [allTransactions, staffMap]);

    // 5. Top işlemler
    const topTransactions = useMemo(() => {
        const txs = allTransactions || [];
        const topIncome = [...txs].filter(t => t.type === "income")
            .sort((a, b) => Number(b.amount) - Number(a.amount)).slice(0, 5);
        const topExpense = [...txs].filter(t => t.type === "expense")
            .sort((a, b) => Number(b.amount) - Number(a.amount)).slice(0, 5);
        return { topIncome, topExpense };
    }, [allTransactions]);

    // 6. Personel performans tablosu
    const staffPerformanceTable = useMemo(() => {
        const txs = allTransactions || [];
        const statsMap = new Map();
        txs.forEach(tx => {
            if (tx.staff_id) {
                if (!statsMap.has(tx.staff_id)) statsMap.set(tx.staff_id, { income: 0, expense: 0, count: 0 });
                const s = statsMap.get(tx.staff_id);
                s.count++;
                if (tx.type === "income") s.income += Number(tx.amount);
                else s.expense += Number(tx.amount);
            }
        });
        return Array.from(statsMap.entries())
            .map(([id, stats]) => ({ id, name: staffMap.get(id) || "Bilinmeyen", ...stats, net: stats.income - stats.expense }))
            .sort((a, b) => b.income - a.income);
    }, [allTransactions, staffMap]);

    const maxStaffIncome = useMemo(
        () => staffPerformanceTable.length > 0 ? Math.max(...staffPerformanceTable.map(s => s.income)) : 1,
        [staffPerformanceTable]
    );

    // ============================================================
    // APEXCHARTS ORTAK SEÇENEKLER
    // ============================================================
    const isDark = document.documentElement.classList.contains("dark");
    const chartTextColor = isDark ? "#94a3b8" : "#64748b";
    const chartGridColor = isDark ? "rgba(148,163,184,0.08)" : "rgba(148,163,184,0.15)";

    const lineChartOptions = useMemo(() => ({
        chart: { type: "area", toolbar: { show: false }, background: "transparent", zoom: { enabled: false } },
        colors: [CHART_COLORS.income, CHART_COLORS.expense],
        stroke: { curve: "smooth", width: 2.5 },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0.0,
                stops: [0, 90, 100],
            },
        },
        grid: { borderColor: chartGridColor, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        xaxis: {
            categories: dailyTrendSeries.categories,
            labels: { style: { colors: chartTextColor, fontSize: "11px", fontWeight: 600 }, rotate: -30 },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { colors: chartTextColor, fontSize: "11px" },
                formatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K ₺` : `${v} ₺`,
            },
        },
        tooltip: {
            theme: isDark ? "dark" : "light",
            y: { formatter: (v) => formatCurrency(v) },
        },
        legend: {
            position: "top", horizontalAlign: "right",
            labels: { colors: chartTextColor },
            markers: { radius: 6 },
        },
        dataLabels: { enabled: false },
    }), [dailyTrendSeries.categories, chartTextColor, chartGridColor, isDark]);

    const donutOptions = useMemo(() => ({
        chart: { type: "donut", background: "transparent" },
        colors: donutSeries.hasData ? [CHART_COLORS.income, CHART_COLORS.expense] : ["#94a3b8", "#e2e8f0"],
        labels: donutSeries.labels,
        legend: { position: "bottom", labels: { colors: chartTextColor }, markers: { radius: 6 } },
        dataLabels: {
            enabled: true,
            style: { fontSize: "11px", fontWeight: 700 },
            formatter: (val) => `${val.toFixed(1)}%`,
            dropShadow: { enabled: false },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: "72%",
                    labels: {
                        show: true,
                        total: {
                            show: true, label: "Net Kar",
                            color: chartTextColor, fontSize: "13px", fontWeight: 700,
                            formatter: () => formatCurrency(summaryData.net_profit),
                        },
                        value: { color: chartTextColor, fontSize: "14px", fontWeight: 700 },
                    },
                },
            },
        },
        tooltip: {
            theme: isDark ? "dark" : "light",
            y: { formatter: (v) => formatCurrency(v) },
        },
        stroke: { width: 0 },
    }), [donutSeries, chartTextColor, isDark, summaryData.net_profit]);

    const monthlyBarOptions = useMemo(() => ({
        chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
        colors: [CHART_COLORS.income, CHART_COLORS.expense],
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 6, borderRadiusApplication: "end" } },
        grid: { borderColor: chartGridColor, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        xaxis: {
            categories: monthlyBarSeries?.categories || [],
            labels: { style: { colors: chartTextColor, fontSize: "11px", fontWeight: 600 } },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { colors: chartTextColor, fontSize: "11px" },
                formatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v,
            },
        },
        tooltip: {
            theme: isDark ? "dark" : "light",
            y: { formatter: (v) => formatCurrency(v) },
        },
        legend: {
            position: "top", horizontalAlign: "right",
            labels: { colors: chartTextColor },
            markers: { radius: 6 },
        },
        dataLabels: { enabled: false },
    }), [monthlyBarSeries, chartTextColor, chartGridColor, isDark]);

    const staffBarOptions = useMemo(() => ({
        chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
        colors: staffBarSeries.colors,
        plotOptions: {
            bar: {
                horizontal: true, barHeight: "65%",
                borderRadius: 6, borderRadiusApplication: "end",
                distributed: true,
            },
        },
        grid: { borderColor: chartGridColor, strokeDashArray: 4, yaxis: { lines: { show: false } } },
        xaxis: {
            labels: {
                style: { colors: chartTextColor, fontSize: "11px" },
                formatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K ₺` : `${v} ₺`,
            },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: {
            labels: { style: { colors: chartTextColor, fontSize: "12px", fontWeight: 700 } },
        },
        tooltip: {
            theme: isDark ? "dark" : "light",
            y: { formatter: (v) => formatCurrency(v) },
        },
        legend: { show: false },
        dataLabels: {
            enabled: true,
            formatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K ₺` : `${v} ₺`,
            style: { fontSize: "10px", fontWeight: 700, colors: ["#fff"] },
            dropShadow: { enabled: false },
        },
    }), [staffBarSeries, chartTextColor, chartGridColor, isDark]);

    // ============================================================
    // LOADING / ERROR STATES
    // ============================================================
    if (loading && allTransactions.length === 0 && !error) {
        return (
            <div className="p-4 md:p-6 space-y-6">
                <div className="h-8 w-64 bg-gray-200 dark:bg-navy-700 rounded-2xl animate-pulse"></div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <KPISkeleton key={i} />)}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {[...Array(2)].map((_, i) => <ChartSkeleton key={i} height={300} />)}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-4">
                    <FiAlertCircle size={28} className="text-red-500" />
                </div>
                <h3 className="font-bold text-navy-700 dark:text-white text-lg mb-2">Veri Yüklenemedi</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{error}</p>
                <button
                    onClick={fetchAnalyticsData}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/30 transition-all"
                >
                    <FiRefreshCw size={14} /> Tekrar Dene
                </button>
            </div>
        );
    }

    // ============================================================
    // ANA RENDER
    // ============================================================
    return (
        <div className="p-4 md:p-6 space-y-6">
            <ToastContainer position="bottom-right" theme="colored" toastClassName="!rounded-2xl !font-bold" />

            <style>{`
        @keyframes fadeInUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .ta-fade { animation: fadeInUp 0.35s ease-out both; }
      `}</style>

            {/* ====== SAYFA BAŞLIĞI ====== */}
            <div className="ta-fade flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center shadow-lg shadow-brand-500/30">
                        <FiBarChart2 className="text-white" size={22} />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-navy-700 dark:text-white">Finansal Analiz</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {activeFilters.filterType === "month"
                                ? `${getMonthName(activeFilters.month)} ${activeFilters.year}`
                                : activeFilters.filterType === "year"
                                    ? `${activeFilters.year} Yılı`
                                    : "Tarih Aralığı"}
                            {" "}• {calculatedMetrics.totalTransactions} işlem
                        </p>
                    </div>
                </div>
                <button
                    onClick={fetchAnalyticsData}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 hover:bg-gray-50 dark:hover:bg-navy-700 transition-all shadow-sm"
                >
                    <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} /> Yenile
                </button>
            </div>

            {/* ====== FİLTRE KARTI ====== */}
            <div className="ta-fade bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden">
                {/* Başlık */}
                <button
                    onClick={() => setFilterOpen(p => !p)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-all"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-brand-100 dark:bg-brand-900/20 flex items-center justify-center">
                            <FiFilter className="text-brand-500" size={16} />
                        </div>
                        <div className="text-left">
                            <h3 className="font-bold text-navy-700 dark:text-white text-base">Analiz Filtreleri</h3>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Dönemi, personeli ve ödeme yöntemini seçin</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>}
                        {filterOpen ? <FiChevronUp className="text-gray-400" size={18} /> : <FiChevronDown className="text-gray-400" size={18} />}
                    </div>
                </button>

                {/* Filtre İçeriği */}
                {filterOpen && (
                    <div className="px-5 pb-5 border-t border-gray-100 dark:border-navy-700 pt-4 space-y-4"
                         style={{ animation: "fadeInUp 0.2s ease-out" }}>

                        {/* 1. Satır: Dönem Tipi + Yıl/Ay/Tarih + Personel */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Dönem Tipi</label>
                                <ModernSelect
                                    name="filterType"
                                    value={tempFilters.filterType}
                                    onChange={handleTempFilterChange}
                                    options={[
                                        { value: "month", label: "Ay Bazlı" },
                                        { value: "year", label: "Yıl Bazlı" },
                                        { value: "range", label: "Tarih Aralığı" },
                                    ]}
                                />
                            </div>

                            {(tempFilters.filterType === "month" || tempFilters.filterType === "year") && (
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Yıl</label>
                                    <ModernSelect
                                        name="year"
                                        value={tempFilters.year}
                                        onChange={handleTempFilterChange}
                                        options={Array.from({ length: 5 }, (_, i) => ({ value: currentYear - i, label: currentYear - i }))}
                                    />
                                </div>
                            )}

                            {tempFilters.filterType === "month" && (
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Ay</label>
                                    <ModernSelect
                                        name="month"
                                        value={tempFilters.month}
                                        onChange={handleTempFilterChange}
                                        options={Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: getMonthName(i + 1) }))}
                                    />
                                </div>
                            )}

                            {tempFilters.filterType === "range" && (
                                <>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Başlangıç</label>
                                        <input
                                            type="date"
                                            name="start_date"
                                            value={tempFilters.start_date}
                                            onChange={handleTempFilterChange}
                                            className="w-full bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 py-2.5 text-sm font-semibold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Bitiş</label>
                                        <input
                                            type="date"
                                            name="end_date"
                                            value={tempFilters.end_date}
                                            onChange={handleTempFilterChange}
                                            className="w-full bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 py-2.5 text-sm font-semibold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all"
                                        />
                                    </div>
                                </>
                            )}

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Personel</label>
                                <ModernSelect
                                    name="staff_id"
                                    value={tempFilters.staff_id}
                                    onChange={handleTempFilterChange}
                                    options={[
                                        { value: "all", label: "Tüm Personeller" },
                                        ...staffList.map(s => ({ value: s.id, label: `${s.name} ${s.surname}` })),
                                    ]}
                                />
                            </div>
                        </div>

                        {/* 2. Satır: İşlem Tipi + Ödeme + Banka + Butonlar */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">İşlem Tipi</label>
                                <ModernSelect
                                    name="type"
                                    value={tempFilters.type}
                                    onChange={handleTempFilterChange}
                                    options={[
                                        { value: "all", label: "Tüm İşlemler" },
                                        { value: "income", label: "Sadece Gelir" },
                                        { value: "expense", label: "Sadece Gider" },
                                    ]}
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Ödeme Yöntemi</label>
                                <ModernSelect
                                    name="payment_method"
                                    value={tempFilters.payment_method}
                                    onChange={handleTempFilterChange}
                                    options={[
                                        { value: "", label: "Tüm Yöntemler" },
                                        { value: "cash", label: "Nakit" },
                                        { value: "card", label: "Kart / Banka" },
                                    ]}
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Banka Hesabı</label>
                                <ModernSelect
                                    name="bank_account_id"
                                    value={tempFilters.bank_account_id}
                                    onChange={handleTempFilterChange}
                                    disabled={tempFilters.payment_method === "cash"}
                                    options={[
                                        { value: "", label: "Tüm Bankalar" },
                                        ...bankList.map(b => ({ value: b.id, label: b.name })),
                                    ]}
                                />
                            </div>

                            <div className="flex gap-2 items-end">
                                <button
                                    onClick={applyFilters}
                                    disabled={loading}
                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/30 transition-all disabled:opacity-60"
                                >
                                    {loading
                                        ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        : <><FiCheck size={14} /> Uygula</>}
                                </button>
                                <button
                                    onClick={resetFilters}
                                    disabled={loading}
                                    className="px-3 py-2.5 rounded-xl text-sm font-bold text-red-500 border border-red-200 dark:border-red-800/40 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all disabled:opacity-50"
                                    title="Sıfırla"
                                >
                                    <FiX size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ====== KPI KARTLARI - SATIR 1 ====== */}
            <div className="ta-fade grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    title="Brüt Gelir"
                    value={formatCurrency(summaryData.total_income)}
                    subtitle={`${calculatedMetrics.incomeCount} İşlem`}
                    icon={<FiTrendingUp size={20} className="text-white" />}
                    gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                />
                <KpiCard
                    title="Toplam Gider"
                    value={formatCurrency(summaryData.total_expense)}
                    subtitle={`${calculatedMetrics.expenseCount} İşlem`}
                    icon={<FiTrendingDown size={20} className="text-white" />}
                    gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                />
                <KpiCard
                    title="Banka Kesintileri"
                    value={formatCurrency(calculatedMetrics.totalFees)}
                    subtitle="Net Gelirden Düşüldü"
                    icon={<FiCreditCard size={20} className="text-white" />}
                    gradient="linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)"
                />
                <KpiCard
                    title="Net Kar / Zarar"
                    value={formatCurrency(summaryData.net_profit)}
                    subtitle={summaryData.net_profit >= 0 ? "Kar" : "Zarar"}
                    icon={<FiDollarSign size={20} className="text-white" />}
                    gradient={summaryData.net_profit >= 0
                        ? "linear-gradient(135deg, #4318FF 0%, #1e12a0 100%)"
                        : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"}
                />
            </div>

            {/* ====== KPI KARTLARI - SATIR 2 ====== */}
            <div className="ta-fade grid grid-cols-1 sm:grid-cols-3 gap-4">
                <KpiCardLight
                    title="Ortalama İşlem"
                    value={formatCurrency(calculatedMetrics.avgTransaction)}
                    subtitle={`${calculatedMetrics.totalTransactions} toplam işlem`}
                    icon={<FiActivity size={18} className="text-blue-600 dark:text-blue-400" />}
                    iconBg="bg-blue-100 dark:bg-blue-900/20"
                />
                <KpiCardLight
                    title="En Yüksek Gelir"
                    value={formatCurrency(calculatedMetrics.maxIncome)}
                    subtitle="Tek işlemde"
                    icon={<FiArrowUpRight size={18} className="text-emerald-600 dark:text-emerald-400" />}
                    iconBg="bg-emerald-100 dark:bg-emerald-900/20"
                    valueColor="text-emerald-700 dark:text-emerald-400"
                />
                <KpiCardLight
                    title="En Yüksek Gider"
                    value={formatCurrency(calculatedMetrics.maxExpense)}
                    subtitle="Tek işlemde"
                    icon={<FiArrowDownRight size={18} className="text-orange-600 dark:text-orange-400" />}
                    iconBg="bg-orange-100 dark:bg-orange-900/20"
                    valueColor="text-orange-700 dark:text-orange-400"
                />
            </div>

            {/* ====== GRAFİKLER - TREND + DONUT ====== */}
            <div className="ta-fade grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Günlük Trend (2/3 genişlik) */}
                <SectionCard
                    title="Gelir & Gider Trend Analizi"
                    subtitle={`${dailyTrendSeries.categories.length} günlük veri`}
                    icon={<FiActivity size={16} className="text-brand-500" />}
                    iconBg="bg-brand-100 dark:bg-brand-900/20"
                    className="lg:col-span-2"
                >
                    <div className="p-4">
                        {dailyTrendSeries.categories.length > 0 ? (
                            <ReactApexChart
                                type="area"
                                series={dailyTrendSeries.series}
                                options={lineChartOptions}
                                height={300}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <FiBarChart2 size={32} className="text-gray-300 dark:text-gray-600 mb-3" />
                                <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">Seçili dönemde veri bulunamadı.</p>
                            </div>
                        )}
                    </div>
                </SectionCard>

                {/* Donut (1/3 genişlik) */}
                <SectionCard
                    title="Gelir / Gider Dağılımı"
                    subtitle={donutSeries.hasData ? "Oran analizi" : "Veri yok"}
                    icon={<FiPieChart size={16} className="text-emerald-500" />}
                    iconBg="bg-emerald-100 dark:bg-emerald-900/20"
                >
                    <div className="p-4">
                        <ReactApexChart
                            type="donut"
                            series={donutSeries.series}
                            options={donutOptions}
                            height={300}
                        />
                    </div>
                </SectionCard>
            </div>

            {/* ====== AYLIK KARŞILAŞTIRMA (SADECE YILLIK) ====== */}
            {activeFilters.filterType === "year" && monthlyBarSeries && (
                <div className="ta-fade">
                    <SectionCard
                        title={`${activeFilters.year} Yılı Aylık Karşılaştırma`}
                        subtitle="Gelir ve gider trendleri"
                        icon={<FiBarChart2 size={16} className="text-amber-500" />}
                        iconBg="bg-amber-100 dark:bg-amber-900/20"
                    >
                        <div className="p-4">
                            <ReactApexChart
                                type="bar"
                                series={monthlyBarSeries.series}
                                options={monthlyBarOptions}
                                height={300}
                            />
                        </div>
                    </SectionCard>
                </div>
            )}

            {/* ====== PERSONEL BAZLI GELİR ANALİZİ ====== */}
            {staffBarSeries.hasData && (
                <div className="ta-fade">
                    <SectionCard
                        title="Personel Bazlı Gelir Analizi (Top 10)"
                        subtitle={`${staffBarSeries.categories.length} personel karşılaştırması`}
                        icon={<FiUser size={16} className="text-blue-500" />}
                        iconBg="bg-blue-100 dark:bg-blue-900/20"
                    >
                        <div className="p-4">
                            <ReactApexChart
                                type="bar"
                                series={staffBarSeries.series}
                                options={staffBarOptions}
                                height={Math.max(250, staffBarSeries.categories.length * 42)}
                            />
                        </div>
                    </SectionCard>
                </div>
            )}

            {/* ====== TOP İŞLEMLER ====== */}
            <div className="ta-fade grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* En Yüksek Gelirler */}
                <SectionCard
                    title="En Yüksek Gelirler (Top 5)"
                    subtitle="Dönem içindeki en büyük gelir kalemleri"
                    icon={<FiArrowUpRight size={16} className="text-emerald-500" />}
                    iconBg="bg-emerald-100 dark:bg-emerald-900/20"
                >
                    {topTransactions.topIncome.length > 0 ? (
                        <div className="p-4 space-y-1">
                            {topTransactions.topIncome.map((tx, idx) => (
                                <TopTxRow key={tx.id} tx={tx} idx={idx} type="income" staffMap={staffMap} bankMap={bankMap} />
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FiArrowUpRight size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Gelir kaydı bulunamadı.</p>
                        </div>
                    )}
                </SectionCard>

                {/* En Yüksek Giderler */}
                <SectionCard
                    title="En Yüksek Giderler (Top 5)"
                    subtitle="Dönem içindeki en büyük gider kalemleri"
                    icon={<FiArrowDownRight size={16} className="text-red-500" />}
                    iconBg="bg-red-100 dark:bg-red-900/20"
                >
                    {topTransactions.topExpense.length > 0 ? (
                        <div className="p-4 space-y-1">
                            {topTransactions.topExpense.map((tx, idx) => (
                                <TopTxRow key={tx.id} tx={tx} idx={idx} type="expense" staffMap={staffMap} bankMap={bankMap} />
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FiArrowDownRight size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Gider kaydı bulunamadı.</p>
                        </div>
                    )}
                </SectionCard>
            </div>

            {/* ====== PERSONEL PERFORMANS ====== */}
            {staffPerformanceTable.length > 0 && (
                <div className="ta-fade">
                    <SectionCard
                        title="Personel Performans Özeti"
                        subtitle={`${staffPerformanceTable.length} personel`}
                        icon={<FiAward size={16} className="text-brand-500" />}
                        iconBg="bg-brand-100 dark:bg-brand-900/20"
                    >
                        {/* Masaüstü Tablo */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                <tr className="bg-gray-50 dark:bg-navy-900/50">
                                    {["Personel", "Gelir", "Gider", "Net", "İşlem Sayısı"].map(h => (
                                        <th key={h} className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {staffPerformanceTable.map((staff) => (
                                    <tr key={staff.id} className="border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-colors">
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                                                    {staff.name.charAt(0)}
                                                </div>
                                                <span className="font-bold text-sm text-navy-700 dark:text-white">{staff.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                          <FiArrowUpRight size={11} /> {formatCurrency(staff.income)}
                        </span>
                                        </td>
                                        <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                          <FiArrowDownRight size={11} /> {formatCurrency(staff.expense)}
                        </span>
                                        </td>
                                        <td className="px-5 py-3.5">
                        <span className={`text-sm font-extrabold ${staff.net >= 0 ? "text-brand-600 dark:text-brand-400" : "text-amber-600 dark:text-amber-400"}`}>
                          {formatCurrency(staff.net)}
                        </span>
                                        </td>
                                        <td className="px-5 py-3.5">
                        <span className="inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-gray-300">
                          {staff.count} işlem
                        </span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobil Kartlar */}
                        <div className="md:hidden p-4 space-y-3">
                            {staffPerformanceTable.map(staff => (
                                <StaffPerfRow key={staff.id} staff={staff} maxIncome={maxStaffIncome} />
                            ))}
                        </div>
                    </SectionCard>
                </div>
            )}
        </div>
    );
};

export default Admin_Transaction_Analytics;